import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../lib/api";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Loader2, AlertTriangle, Sparkles, UserPlus } from "lucide-react";

const ROLE_LABELS = {
  juriste: "Juriste / Avocat",
  parlementaire: "Parlementaire",
  gouvernement: "Gouvernement",
  ong: "ONG / Société civile",
  chercheur: "Chercheur",
  citoyen: "Citoyen",
};

export default function Invite() {
  const { token } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/invitations/public/${token}`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status === 404 ? "Invitation introuvable" : "Erreur");
        return r.json();
      })
      .then((d) => setData(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const goRegister = () => {
    // Encoded in URL so the Register page can pre-fill
    nav(`/register?invite=${token}&email=${encodeURIComponent(data.email)}&role=${data.role}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0D1B12 0%, #1A3C5E 100%)", color: "white" }}>
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ background: "linear-gradient(135deg, #0D1B12 0%, #1A3C5E 100%)" }}
        data-testid="invite-error"
      >
        <div className="text-center max-w-md" style={{ color: "white" }}>
          <AlertTriangle size={40} style={{ color: "#E67E22" }} className="mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold mb-2">Invitation invalide</h1>
          <p className="opacity-80 mb-6">{error || "Ce lien n'est plus disponible."}</p>
          <Button onClick={() => nav("/")} className="rounded-sm font-semibold"
            style={{ background: "#D4A017", color: "#0D1B12" }}>
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  if (data.used) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ background: "linear-gradient(135deg, #0D1B12 0%, #1A3C5E 100%)" }}
        data-testid="invite-used"
      >
        <div className="text-center max-w-md" style={{ color: "white" }}>
          <Sparkles size={40} style={{ color: "#27AE60" }} className="mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold mb-2">Invitation déjà utilisée</h1>
          <p className="opacity-80 mb-6">
            Cette invitation a déjà été acceptée. Vous pouvez vous connecter directement.
          </p>
          <Button onClick={() => nav("/login")} className="rounded-sm font-semibold"
            style={{ background: "#D4A017", color: "#0D1B12" }} data-testid="invite-goto-login">
            Se connecter
          </Button>
        </div>
      </div>
    );
  }

  if (data.expired) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ background: "linear-gradient(135deg, #0D1B12 0%, #1A3C5E 100%)" }}
        data-testid="invite-expired"
      >
        <div className="text-center max-w-md" style={{ color: "white" }}>
          <AlertTriangle size={40} style={{ color: "#E67E22" }} className="mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold mb-2">Invitation expirée</h1>
          <p className="opacity-80 mb-6">
            Cette invitation a expiré (valable 7 jours). Demandez-en une nouvelle à{" "}
            <b style={{ color: "#D4A017" }}>{data.inviter_email}</b>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 py-12"
      style={{ background: "linear-gradient(135deg, #0D1B12 0%, #1A3C5E 100%)" }}
      data-testid="invite-page"
    >
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="inline-block mb-4"><Logo size="lg" /></div>
        </div>
        <div
          className="rounded-sm p-8 border"
          style={{
            background: "hsl(var(--card))",
            borderColor: "rgba(212, 160, 23, 0.4)",
            boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-70 mb-3 font-mono">
            ✉ Invitation personnelle
          </div>
          <h1 className="font-display text-3xl font-bold mb-2">
            Vous êtes invité(e) par
          </h1>
          <div className="font-mono text-lg mb-6" style={{ color: "#D4A017" }}>
            {data.inviter_email}
          </div>

          {data.message && (
            <div className="border-l-2 pl-4 py-2 mb-6 italic text-sm opacity-90"
              style={{ borderColor: "#D4A017" }}>
              « {data.message} »
            </div>
          )}

          <p className="text-sm opacity-80 leading-relaxed mb-6">
            Rejoignez <b>RESOURCES-ANALYZER PRO</b> — l'outil d'analyse des conventions
            d'exploitation des ressources naturelles. Votre compte sera pré-configuré avec :
          </p>

          <div className="space-y-2 mb-8">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-[10px] uppercase opacity-60 w-16 font-mono">Email</span>
              <span className="font-semibold">{data.email}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-[10px] uppercase opacity-60 w-16 font-mono">Rôle</span>
              <span className="font-semibold">{ROLE_LABELS[data.role] || data.role}</span>
            </div>
          </div>

          <Button onClick={goRegister} className="w-full rounded-sm font-semibold"
            style={{ background: "#1B4332", color: "white" }}
            data-testid="invite-accept-btn">
            <UserPlus size={14} className="mr-2" />
            Accepter l'invitation et créer mon compte
          </Button>

          <div className="text-center text-xs opacity-60 mt-6 font-mono">
            Expire le {new Date(data.expires_at).toLocaleDateString("fr-FR")}
          </div>
        </div>
        <div className="text-center text-[10px] mt-6 opacity-50" style={{ color: "white" }}>
          « La transparence contractuelle au service du peuple »
        </div>
      </div>
    </div>
  );
}
